import cx from "classnames";

import { PaymentMethodOption, WalletType } from "common/types";
import { getPaymentProviderImageSrc } from "common/utils/images";
import { titleCased } from "common/utils/strings";
import Image from "common/components/Image";
import { Optional } from "common/utils/types";

import { walletDisplayText } from "client/payments/utils";

import paymentStyles from "../../styles/PaymentMethod.module.less";

const renderPaymentMethodLabelAndDescription = (
  paymentMethod: PaymentMethodOption,
  existingWallet: Optional<WalletType>,
) => {
  if (paymentMethod.id === "__klarna__") {
    return { label: "Pay with Klarna", description: `${paymentMethod.label}` };
  }
  if (paymentMethod.id === "__wallet__") {
    return {
      label:
        existingWallet === paymentMethod.wallet
          ? `Pay with new ${walletDisplayText(paymentMethod.wallet)} method`
          : `Pay with ${walletDisplayText(paymentMethod.wallet)}`,
      description: "",
    };
  }

  const brandLabel = titleCased(paymentMethod.brand) || "Card";
  const expiry =
    paymentMethod.expiration_month && paymentMethod.expiration_year
      ? ` · Expires ${paymentMethod.expiration_month}/${paymentMethod.expiration_year}`
      : "";
  return {
    label: paymentMethod.last_four ? `${brandLabel}  ****${paymentMethod.last_four}` : brandLabel,
    description: `${paymentMethod.name}${expiry}`,
  };
};

const renderPaymentMethodImage = (
  paymentMethod: PaymentMethodOption,
  otherExistingWallet: Optional<WalletType>,
  className: Optional<string>,
) => {
  const [image, needsBorder] = getPaymentProviderImageSrc(paymentMethod) || [undefined, true];

  if (paymentMethod.is_klarna) {
    return (
      <Image
        className={cx(paymentStyles.image, needsBorder && paymentStyles.withBorder)}
        src={image}
        alt="Klarna"
      />
    );
  }

  const { label } = renderPaymentMethodLabelAndDescription(paymentMethod, otherExistingWallet);

  return (
    <div className={cx(paymentStyles.image, className, needsBorder && paymentStyles.withBorder)}>
      {image ? (
        <Image src={image} alt={label} />
      ) : (
        <div className={paymentStyles.text}>
          {titleCased(paymentMethod.wallet || paymentMethod.brand)}
        </div>
      )}
    </div>
  );
};

type PaymentMethodProps = {
  method: PaymentMethodOption;
  styles?: { label: string; description: string; image?: string };
  otherExistingWallet?: Optional<WalletType>;
};

const PaymentMethod = ({ method, styles, otherExistingWallet }: PaymentMethodProps) => {
  const { label, description } = renderPaymentMethodLabelAndDescription(
    method,
    otherExistingWallet,
  );
  const image = renderPaymentMethodImage(method, otherExistingWallet, styles?.image);
  return (
    <div className={paymentStyles.container}>
      {image}
      <div>
        <div className={cx(styles?.label, paymentStyles.label)}>{label}</div>
        <div className={cx(styles?.description)}>{description}</div>
      </div>
    </div>
  );
};

type PaymentMethodShortProps = {
  method: PaymentMethodOption;
  className?: string;
  existingWallet?: Optional<WalletType>;
};

const PaymentMethodShort = ({ method, className, existingWallet }: PaymentMethodShortProps) => {
  const image = renderPaymentMethodImage(method, existingWallet, className);
  const { label } = renderPaymentMethodLabelAndDescription(method, existingWallet);
  return (
    <div className={cx(paymentStyles.container, className)}>
      {image}
      <div>{label}</div>
    </div>
  );
};

export default Object.assign(PaymentMethod, { Short: PaymentMethodShort });
